import BaseUI from "../base-ui.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import UiUtils from "../../utils/ui-utils.ts";
import Route from "../../enums/route.ts";
import LogUtils from "../../utils/log-utils.ts";
import User from "../../models/user.ts";

export default class LobbyFragment extends BaseUI {
  private readonly usernameElements: HTMLParagraphElement[];

  constructor(
    state: State,
    apiClient: EatOutApiClient,
    navigateCallback: (route: Route) => void,
  ) {
    super("eat-out-lobby-fragment", state, apiClient, navigateCallback);

    this.usernameElements = [];
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    for (const usernameElement of this.usernameElements) {
      newElements.push(usernameElement);
    }

    return super.render(newElements);
  }

  public addUser(user: User): void {
    // set name and ID
    const usernameParagraph: HTMLParagraphElement = UiUtils.paragraph(user.name);
    usernameParagraph.dataset.id = user.id;

    // add to this collection for easy lookup during removal
    this.usernameElements.push(usernameParagraph);

    // add it to the screen, visually
    this.container.appendChild(usernameParagraph);
  }

  public removeUser(userId: string): void {
    // find the username element associated with the given username
    const usernameParagraph: HTMLParagraphElement | undefined = this.usernameElements.find(
      (usernameElement: HTMLParagraphElement): boolean => {
        // make sure it actually contains an ID stored within 'dataset'
        const usernameElementId: string | undefined = usernameElement.dataset.id;
        if (usernameElementId === undefined) {
          LogUtils.error(
            "attempted to remove 'usernameElement' from Lobby screen, but it didn't contain a '.dataset.id'",
          );
        }

        // we found the element that matched the name
        return usernameElementId === userId;
      },
    );
    if (usernameParagraph === undefined) {
      LogUtils.error(`'usernameParagraph' from 'usernameElements' with user ID '${userId} could not be found'`);
    }

    // find the index of the username element in order to splice it out
    const index: number = this.usernameElements.indexOf(usernameParagraph);
    if (index === -1) {
      LogUtils.error(`could not find index of 'usernameParagraph' with ID '${userId}'`);
    }

    // remove from in-memory cache
    this.usernameElements.splice(index, 1);

    // remove from UI container div
    if (usernameParagraph.parentElement !== this.container) {
      // TODO - should this be an error message?
      LogUtils.warn(
        this.debug,
        "attempted to remove `usernameParagraph` from `this.container`, but it doesn't exist there",
      );
      return;
    }
    this.container.removeChild(usernameParagraph);
  }
}
